import { Assets, Components } from '@sendum/core'
import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Select, { ValueType } from 'react-select'

import * as ShipmentSelectorActions from '../store/ShipmentSelectorActions'
import { ShipmentSelectorRootState } from '../store/ShipmentSelectorStore'
import { TFilter } from '../types/TFilter'
import s from './ShipmentSelectorControlPanel.module.scss'

type OptionType = { value: string; label: string }

const filters: OptionType[] = [
    { label: 'Master Tracking Number', value: 'masterTrackingNo' },
    { label: 'Client', value: 'name' },
    { label: 'Container', value: 'containerId' },
    { label: 'Device Id', value: 'deviceId' },
    { label: 'Placement Site', value: 'origin' },
    { label: 'Kite Site', value: 'kiteSite' },
    { label: 'Infusion Site', value: 'infusionSite' },
    { label: 'Return Site', value: 'destination' }
]

const operators: OptionType[] = [
    { label: 'Equal', value: 'eq' },
    { label: 'Not equal', value: 'neq' },
    { label: 'Contains', value: 'cnt' },
    { label: 'Not Contains', value: 'ncnt' }
]

interface IShipmentSelectorControlPanelProps {
    onChange: () => void
}

export const ShipmentSelectorControlPanel = ({ onChange }: IShipmentSelectorControlPanelProps): JSX.Element => {
    const dispatch = useDispatch()

    const filter = useSelector<ShipmentSelectorRootState, TFilter>(
        (state: ShipmentSelectorRootState) => state.ShipmentSelector.filter,
        shallowEqual
    )
    const search = useSelector<ShipmentSelectorRootState, string | undefined>(
        (state: ShipmentSelectorRootState) => state.ShipmentSelector.search,
        shallowEqual
    )
    const isLoadingDevices = useSelector<ShipmentSelectorRootState, boolean | undefined>(
        (state: ShipmentSelectorRootState) => state.ShipmentSelector.isLoading,
        shallowEqual
    )

    const selected = useMemo(
        (): OptionType => filters.filter((option: OptionType) => filter?.selected === option.value)[0],
        [filter]
    )
    const operator = useMemo(
        (): OptionType => operators.filter((option: OptionType) => filter?.operator === option.value)[0],
        [filter]
    )

    const searchInputOnChangeHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            dispatch(ShipmentSelectorActions.setSearch(event.target.value))
        },
        [dispatch]
    )

    const inputHandler = useCallback((): void => {
        if (filter.selected && search) {
            dispatch(
                ShipmentSelectorActions.setFilter({
                    selected: undefined,
                    operator: undefined,
                    sortOrder: undefined,
                    sortBy: undefined,
                    list: [...filter.list, { name: filter.selected, operator: filter.operator, value: search }]
                })
            )
            dispatch(ShipmentSelectorActions.setSearch(''))

            onChange()
        }
    }, [dispatch, filter, search, onChange])

    const searchInputOnKeyPressHandler = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>): void => {
            if (event.key === 'Enter') {
                event.preventDefault()
                inputHandler()
            }
        },
        [inputHandler]
    )

    const inputImgOnClickHandler = useCallback(
        (event: React.MouseEvent<HTMLImageElement>): void => {
            event.preventDefault()
            inputHandler()
        },
        [inputHandler]
    )

    const onChangeFilterHandler = useCallback(
        (value: ValueType<OptionType, boolean>): void => {
            dispatch(
                ShipmentSelectorActions.setFilter({
                    ...filter,
                    selected: value === null ? undefined : (value as OptionType).value,
                    operator: 'eq'
                })
            )
        },
        [dispatch, filter]
    )

    const onChangeOperatorHandler = useCallback(
        (value: ValueType<OptionType, boolean>): void => {
            dispatch(
                ShipmentSelectorActions.setFilter({
                    ...filter,
                    operator: value === null ? undefined : (value as OptionType).value
                })
            )
        },
        [dispatch, filter]
    )

    const onClickDeleteFilterHandler = useCallback(
        (index: number) => () => {
            const list = [...filter.list]
            list.splice(index, 1)
            dispatch(ShipmentSelectorActions.setFilter({ ...filter, list }))
            onChange()
        },
        [dispatch, filter, onChange]
    )

    const customStyles = {
        control: (styles: any, state: any) => ({
            ...styles,
            border: '1px solid rgb(204, 204, 204)',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid rgb(179, 179, 179);'
            }
        }),
        option: (base: any, state: any) => ({
            ...base,
            color: state.isSelected ? 'white' : 'var(--sw-sensorDate-gray)',
            backgroundColor: state.isSelected ? 'var(--sw-sensorDate-gray)' : 'white',
            '&:hover': { backgroundColor: state.isSelected ? 'var(--sw-sensorDate-gray)' : 'rgb(0, 0, 0, 0.12)' },
            cursor: 'pointer'
        })
    }

    return (
        <div className={s.shipmentSelectorControlPanel} data-testid="shipment-selector-control-panel">
            <div className={`${s.shipmentSelectorControlPanel__controls} ${s.controls}`}>
                <div className={s.controls__row}>
                    <div className={s.controls__filter}>
                        <Components.Label text={'Filter'}>
                            <Select
                                defaultValue={selected ?? null}
                                value={selected ?? null}
                                options={filters.filter(f => !filter.list.map(_ => _.name).includes(f.value))}
                                onChange={onChangeFilterHandler}
                                isClearable={false}
                                isSearchable={false}
                                isDisabled={isLoadingDevices}
                                styles={customStyles}
                            />
                        </Components.Label>
                    </div>
                    <div className={s.controls__filter}>
                        <Components.Label text={'Operator'}>
                            <Select
                                defaultValue={operator ?? null}
                                value={operator ?? null}
                                options={operators}
                                onChange={onChangeOperatorHandler}
                                isSearchable={false}
                                isDisabled={isLoadingDevices || !filter.selected}
                                styles={customStyles}
                            />
                        </Components.Label>
                    </div>
                    <div className={s.controls__search}>
                        <Components.Label
                            text={`Search by ${filters.find(_ => _.value === filter.selected)?.label ?? '...'}`}
                        >
                            <input
                                type="text"
                                className={s.input}
                                placeholder={'Start typing...'}
                                value={search}
                                onChange={searchInputOnChangeHandler}
                                onKeyPress={searchInputOnKeyPressHandler}
                                disabled={isLoadingDevices || !filter.selected || !filter.operator}
                            />
                            <div className={s.controls__search_inputImgContainer}>
                                {search && filter.selected && (
                                    <img
                                        className={s.controls__search_inputImg}
                                        src={Assets.Enter}
                                        alt={'Enter'}
                                        onClick={inputImgOnClickHandler}
                                    />
                                )}
                            </div>
                        </Components.Label>
                    </div>
                </div>
            </div>
            {filter.list.length > 0 && (
                <div className={`${s.shipmentSelectorControlPanel__list} ${s.list}`}>
                    <div className={s.list__row}>
                        {filter.list.map((f, index) => (
                            <div className={s.list__item} key={f.name}>
                                {filters.find(_ => _.value === f.name)?.label}{' '}
                                {operators.find(_ => _.value === f.operator)?.label.toLocaleLowerCase()} (
                                {f.value && f.value?.length > 20 ? `${f.value?.substring(0, 20)}...` : f.value})
                                <div className={s.closeIcon}>
                                    <img alt={'close'} src={Assets.Close} onClick={onClickDeleteFilterHandler(index)} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
